import { PwaUrlBuilder } from '@wetteronline/pwa-urls';

import { Faq, HtmlFaqItem } from './faq.types';
import { locale, translationsPromise } from './locale';

window.addEventListener('DOMContentLoaded', () => void init());

async function init() {
  const categories = new Map<string, HtmlFaqItem[]>();
  await loadFaqContent(categories);

  // set translations
  const translations = await translationsPromise;
  const title = document.getElementById('title');
  if (title) {
    title.innerText = translations['faq_title'];
  }
  const searchInput = document.getElementById('search-input');
  if (searchInput)
    searchInput.setAttribute(
      'placeholder',
      translations['faq_search_box_placeholder'],
    );

  const faqTitle = document.getElementById('faq-title');
  if (faqTitle) {
    faqTitle.innerText = translations['faq_popular_categories_title'];
  }

  const supportButton = document.getElementById('support-button');
  if (supportButton) {
    supportButton.innerText = translations['faq_contact_support_team'];
    const builder = PwaUrlBuilder.fromLocale(
      `${locale.language}-${locale.region}`,
    );
    supportButton.setAttribute(
      'href',
      `https://${builder.subDomain}.${builder.hostname}/app-feedback?type=support`,
    );
    supportButton.setAttribute('target', `_blank`);
  }

  const noResultsTitle = document.getElementById('no-results-title');
  if (noResultsTitle) {
    noResultsTitle.innerText = translations['faq_results_not_found_title'];
  }
  const noResults = document.getElementById('no-results');
  if (noResults) {
    noResults.innerText = translations['faq_results_not_found_text'];
  }

  searchInput?.addEventListener('input', () => {
    const needle = normalize((searchInput as HTMLInputElement).value ?? '');
    let firstMatch: HTMLDetailsElement | null = null;

    categories.forEach((items, category) => {
      let categoryHasMatch = false;
      items.forEach((item) => {
        const element = item.element as HTMLDetailsElement;
        const hay = normalize(
          item.data.title +
            ' ' +
            (element.querySelector('.answer')?.textContent ?? ''),
        );
        const hit = needle.length === 0 || hay.includes(needle);
        element.style.display = hit ? '' : 'none';

        // mark results
        const summary = element.querySelector('summary');
        if (summary) {
          // Restore original text (without marks)
          summary.textContent = item.data.title;
          if (needle && hit) {
            highlightText(summary as HTMLElement, needle);
          }
        }
        const answerDiv = element.querySelector('.answer');
        if (answerDiv) {
          // Restore original text (without marks)
          answerDiv.innerHTML = item.data.attributes.ANSWER_TEXT.Default;
          if (needle && hit) {
            highlightText(answerDiv as HTMLElement, needle);
          }
        }
        if (hit && !firstMatch) {
          firstMatch = element;
          element.open = true;
        } else {
          element.open = false;
        }
        if (hit) categoryHasMatch = true;
      });
      // hide categories without matches
      const categoryElements: NodeListOf<HTMLDetailsElement> =
        document.querySelectorAll(`.category[data-faq-category="${category}"]`);
      categoryElements.forEach((catEl) => {
        catEl.open = categoryHasMatch && needle.trim().length > 0;
        catEl.style.display = categoryHasMatch ? '' : 'none';
      });
    });

    if (noResults && noResultsTitle && faqTitle) {
      if (firstMatch == null && needle.length > 0) {
        // show no results message
        noResults.style.display = '';
        noResultsTitle.style.display = '';
        noResults.innerText =
          translations['faq_results_not_found_text'] +
          ' ' +
          (searchInput as HTMLInputElement).value;
        faqTitle.style.display = 'none';
      } else {
        noResults.style.display = 'none';
        noResultsTitle.style.display = 'none';
        faqTitle.style.display = '';
      }
    }
  });
}

async function loadFaqContent(categories: Map<string, HtmlFaqItem[]>) {
  const faqContent = document.getElementById('faq-content');
  if (!faqContent) return;
  let faqData: Faq | undefined = undefined;
  for (const file of [
    locale.language + '-' + locale.region,
    locale.language.substring(0, 2),
    'en',
  ]) {
    try {
      faqData = (await import(`../assets/data/${file}.json`)) as Faq;
      break;
    } catch {
      // try next
    }
  }
  if (faqData === undefined) return;

  // order items by categories
  for (const faqItem of faqData.contents) {
    for (const categoryArr of faqItem.categories) {
      for (const category of categoryArr) {
        let item = categories.get(category);
        if (!item) {
          item = [];
          categories.set(category, item);
        }
        item.push({ data: faqItem });
      }
    }
  }
  // add faq to dom
  for (const [category, items] of categories) {
    const categoryDetails = document.createElement('details');
    categoryDetails.classList.add('category');
    categoryDetails.setAttribute('data-faq-category', category);
    const categoryTitle = document.createElement('summary');
    categoryTitle.textContent = category;
    categoryDetails.appendChild(categoryTitle);
    faqContent.appendChild(categoryDetails);

    for (const item of items) {
      const details = document.createElement('details');
      details.setAttribute('data-faq-id', item.data.id.toString());
      details.setAttribute('data-faq-category', category);
      details.innerHTML = `
          <summary>${item.data.title}</summary>
          <div class="answer">${item.data.attributes.ANSWER_TEXT.Default}</div>
        `;
      categoryDetails.appendChild(details);
    }
  }
  // save references to elements (this has to be done after all elements are
  // in the DOM, otherwise the references will become invalid)
  for (const [category, items] of categories) {
    for (const item of items) {
      item.element = document.querySelector(
        `[data-faq-id="${item.data.id}"][data-faq-category="${category}"]`,
      ) as HTMLElement;
    }
  }
}

function normalize(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function highlightText(element: HTMLElement, needle: string) {
  if (!needle) return;
  // we walk through all text nodes because we do not want to mess up existing HTML tags
  const walk = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
  const regex = new RegExp(`(${needle})`, 'gi');
  const nodes: Text[] = [];
  while (walk.nextNode()) {
    nodes.push(walk.currentNode as Text);
  }
  nodes.forEach((node) => {
    const parent = node.parentNode;
    if (!parent) return;
    if (regex.test(node.textContent || '')) {
      // Replace text with marked text
      const frag = document.createDocumentFragment();
      let lastIndex = 0;
      const text = node.textContent || '';
      text.replace(regex, (match, p1, offset: number) => {
        if (offset > lastIndex) {
          frag.appendChild(
            document.createTextNode(text.slice(lastIndex, offset)),
          );
        }
        const mark = document.createElement('mark');
        mark.textContent = match;
        frag.appendChild(mark);
        lastIndex = offset + match.length;
        return match;
      });
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      parent.replaceChild(frag, node);
    }
  });
}
